import { useRef, type SyntheticEvent } from 'react';
import { Icon } from '../../components/ui/Icon';
import type { AllegatoNelCampo, CampoAllegati } from './allegati';
import {
  ACCETTA_COMPUTER, ACCETTA_TELEFONO, PAROLE_ALLEGATI, altFoto, pagineDelPdf, titoloPezzo, togliNome,
} from './allegatiTesto';
import './allegati.css';

/**
 * Gli allegati sopra il campo della chat di MUNE (29/10/2026, fetta 2F «Allegati» della Fase 2;
 * os-planning/ai/fase-2f-allegati.md, «Interfaccia»): la graffetta, la striscia dei pezzi con la
 * riga rossa, il riquadro di rilascio. Lo stato sta in allegati.ts (useAllegatiCampo), le parole
 * in allegatiTesto.ts: un .tsx esporta solo componenti (Fast Refresh).
 *
 * Computer e telefono hanno la stessa striscia con una UX diversa: al computer i pezzi vanno a
 * capo, la x sta sull'angolo e le frasi rosse sono al massimo tre; al telefono una riga sola che
 * scorre di lato, la x dentro il pezzo col bersaglio da 44 e una frase rossa sola. Le misure le fa
 * il CSS (allegati.css); `mobile` serve solo a cambiare struttura.
 */

/**
 * x e «Riprova» non prendono il fuoco: a tastiera aperta togliere un pezzo non chiude la tastiera
 * e il pannello non salta. Anche su mousedown, che è dove il computer sposta il fuoco.
 */
const trattieni = (e: SyntheticEvent) => e.preventDefault();

/**
 * La graffetta a sinistra del campo, «Allega foto o PDF». Apre la scelta dei file del sistema: al
 * computer i formati che il browser sa ridisegnare, al telefono `image/*` e i PDF, senza
 * `capture` (iOS mostra «Libreria foto», «Scatta foto», «Scegli file»). Dopo la scelta l'input si
 * svuota, se no lo stesso file scelto due volte non scatterebbe; il fuoco al campo lo rimette il
 * pannello, solo col mouse.
 */
/** `piu`: tipi in più da far scegliere, dalla 2G (29/10/2026) le estensioni dei vocali. */
export function GraffettaAllegati({ mobile, onFile, piu }: { mobile: boolean; onFile: (f: File[]) => void; piu?: string }): JSX.Element {
  const scelta = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        className="btn btn-ghost btn-icon alg-graffetta"
        title={PAROLE_ALLEGATI.graffetta}
        aria-label={PAROLE_ALLEGATI.graffetta}
        onClick={() => scelta.current?.click()}
      >
        <Icon name="paperclip" size={16} />
      </button>
      <input
        ref={scelta}
        type="file"
        className="alg-scelta"
        tabIndex={-1}
        aria-hidden="true"
        multiple
        accept={`${mobile ? ACCETTA_TELEFONO : ACCETTA_COMPUTER}${piu ? `,${piu}` : ''}`}
        onChange={(e) => {
          const file = Array.from(e.currentTarget.files ?? []);
          e.currentTarget.value = '';
          if (file.length) onFile(file);
        }}
      />
    </>
  );
}

/** La barra da 2 px in fondo al pezzo mentre il file parte: inchiostro sul filo. */
function Barra({ quanto }: { quanto: number }) {
  return (
    <span className="alg-barra" aria-hidden="true">
      <span className="alg-barra-piena" style={{ transform: `scaleX(${quanto})` }} />
    </span>
  );
}

/** Un pezzo della striscia: la miniatura della foto, o il PDF col nome e le pagine; la x sempre. */
function PezzoStriscia({ a, onTogli }: { a: AllegatoNelCampo; onTogli: () => void }) {
  const foto = a.tipo === 'foto';
  return (
    <li
      className="alg-pezzo"
      data-tipo={a.tipo}
      data-stato={a.stato}
      data-sbagliato={a.sbagliato ? 'true' : undefined}
      title={titoloPezzo(a)}
    >
      {foto ? (
        a.anteprima
          ? <img className="alg-foto" src={a.anteprima} alt={altFoto(a.nome)} draggable={false} />
          : <span className="alg-vuota"><Icon name="image" size={18} /></span>
      ) : (
        <span className="alg-pdf">
          <Icon name="file" size={16} className="alg-icona" />
          <span className="alg-testo">
            <span className="alg-nome">{a.nome}</span>
            <span className="alg-sotto">{a.stato === 'carico' ? PAROLE_ALLEGATI.caricoPdf : pagineDelPdf(a.pagine)}</span>
          </span>
        </span>
      )}
      {a.stato === 'carico' && a.avanzamento !== null && <Barra quanto={a.avanzamento} />}
      <button
        type="button"
        className="alg-x m-hit"
        title={togliNome(a.nome)}
        aria-label={togliNome(a.nome)}
        onPointerDown={trattieni}
        onMouseDown={trattieni}
        onClick={onTogli}
      >
        <Icon name="x" size={12} strokeWidth={2} />
      </button>
    </li>
  );
}

/**
 * La striscia sopra la riga del campo, dentro il piede: i pezzi e sotto la riga rossa, una frase
 * per file, la più nuova in cima (al computer al massimo tre, al telefono una). Un pezzo fermo per
 * la rete ha accanto alla sua frase «Riprova»; con ALLEGATI_CONVERSAZIONE la frase ha «Nuova
 * conversazione», che fa quello che fa il + dell'intestazione e lascia campo e striscia come sono.
 * Senza pezzi e senza frasi non c'è.
 */
export function StrisciaAllegati({ campo, mobile, onNuovaConversazione }: {
  campo: CampoAllegati;
  mobile: boolean;
  onNuovaConversazione: () => void;
}): JSX.Element | null {
  const { allegati, errori } = campo;
  if (!allegati.length && !errori.length) return null;
  return (
    <div className="alg-campo" data-mobile={mobile ? 'true' : undefined}>
      {allegati.length > 0 && (
        <ul className="alg-striscia">
          {allegati.map((a) => <PezzoStriscia key={a.chiave} a={a} onTogli={() => campo.togli(a.chiave)} />)}
        </ul>
      )}
      {errori.length > 0 && (
        <div className="alg-errori">
          {errori.map((r) => (
            <p key={r.n} className="alg-errore">
              <span className="alg-errore-frase">{r.frase}</span>
              {r.riprova && r.chiave && (
                <button
                  type="button"
                  className="alg-link"
                  onPointerDown={trattieni}
                  onMouseDown={trattieni}
                  onClick={() => { if (r.chiave) campo.riprova(r.chiave); }}
                >
                  {PAROLE_ALLEGATI.riprova}
                </button>
              )}
              {r.nuova && (
                <button
                  type="button"
                  className="alg-link"
                  onClick={() => {
                    campo.togliRiga(r.n);
                    onNuovaConversazione();
                  }}
                >
                  {PAROLE_ALLEGATI.nuovaConversazione}
                </button>
              )}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Il riquadro piatto che copre corpo e piede mentre sopra il pannello passa un file (computer):
 * fondo --surface, bordo tratteggiato rientrato, la graffetta e «Lascia qui le foto e i PDF da
 * far leggere a MUNE». Non prende il mouse: il rilascio lo riceve la zona sotto (useTrascinaFile).
 */
export function RilascioAllegati(): JSX.Element {
  return (
    <div className="alg-rilascio" aria-hidden="true">
      <div className="alg-rilascio-box">
        <Icon name="paperclip" size={20} />
        <span>{PAROLE_ALLEGATI.rilascio}</span>
      </div>
    </div>
  );
}
